// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import {
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ChangeDetectorRef,
  SimpleChanges,
  ViewChild,
  ElementRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ImageModel } from '../../models/imageData.model';
import { LocationStrategy } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import { Observable } from 'rxjs';
import { ZoomDetectorService } from '../../../services/zoom-detector.service';
import { ScrollLockService } from '../../../services/scroll-lock.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'gallery-grid',
  templateUrl: './gallery-grid.component.html',
  styleUrls: ['./gallery-grid.component.css'],
  standalone: false
})
export class GalleryGridComponent {
  private destroyRef = inject(DestroyRef);
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  @Input('images') images: ImageModel[] = [];
  @Input('showShareOption') showShareOption: boolean = false;
  @Input('showAdminDelete') showAdminDelete: boolean = false;
  @Input('showDeleteIcon') showDeleteIcon: boolean = false;
  @Input('allowDownload') allowDownload: boolean = false;
  @Input('showDownload') showDownload: boolean = false;
  @Input('allowRearrange') allowRearrange: boolean = false;
  @Input('showMultiSelectDelete') showMultiSelectDelete: boolean = false;
  @Input('scrollType') scrollType: 'page' | 'scroll' = 'scroll';
  trackById = (_: number, item: ImageModel) => item.id;

  @Output() onScrollEnd = new EventEmitter<void>();
  @Output() onLightboxEnd = new EventEmitter<void>();
  @Output() onRearrange = new EventEmitter<{ imageId: string, displayOrder: number }>();

  @Output('onShare') onShare = new EventEmitter<string>();
  @Output('onAdminDelete') onAdminDelete = new EventEmitter<ImageModel>();
  @Output('onDelete') onDelete = new EventEmitter<ImageModel>();
  @Output('downloadSelectedImages') downloadSelectedImages = new EventEmitter<ImageModel[]>();
  @Output('deleteSelectedImages') deleteSelectedImages = new EventEmitter<string[]>();
  @Output('deleteSelectedMyImages') deleteSelectedMyImages = new EventEmitter<string[]>();
  @Output('onLeftSwipe') onLeftSwipe = new EventEmitter<string[]>();
  @Output('onRightSwipe') onRightSwipe = new EventEmitter<string[]>();
  @Output() lightboxToggled = new EventEmitter<boolean>();

  lightboxImage: ImageModel | null = null;
  touchPoints = 0;
  touchStartX = 0;
  touchStartY = 0;
  swipeThreshold = 10;

  modalStatePushed: boolean = false;
  currentIndex = 0;
  showImageNavButtons = true;
  disableSwipeNav = false;
  lightboxOpen: boolean = false;
  longPressTimer: any;
  longPressTriggered = false;
  longPressDelay = 500;

  dragIndex: number | null = null;
  targetIndex: number | null = null;
  dragImageId: string | null = null;
  dragDisplayOrder: number | null = null;
  newDisplayOrder: number | null = null;
  isDragging: boolean = false;

  isReorder = false;

  isLoggedIn$!: Observable<boolean>;
  isAdmin$!: Observable<boolean>;

  zoom = 1;

  parallelImageLoad = 1;

  get IsAdmin() {
    return this.authService.isAdmin;
  }

  get imageSelected(): boolean {
    const isSelected = this.images.some(x => x.selected);

    if (isSelected && this.allowRearrange) {
      this.scrollLockService.lockScroll();
    } else {
      this.scrollLockService.unlockScroll();
    }

    return isSelected;
  }


  constructor(
    private _location: LocationStrategy,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private zoomService: ZoomDetectorService,
    private scrollLockService: ScrollLockService,
    private snackBar: MatSnackBar
  ) {
    this._location.onPopState(() => {
      if (this.lightboxOpen) {
        this.onLightboxClose();
      }
    });
  }

  ngOnInit() {
    setTimeout(() => {
      this.isLoggedIn$ = this.authService.isLoggedIn$;
      this.isAdmin$ = this.authService.isAdmin$;
    });

    this.zoomService.zoom$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(level => {
      this.zoom = level;
    });
  }

  ngOnDestroy() {
    document.body.style.overflow = 'auto';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['images'] && this.images?.length) {
      if (this.scrollType == 'page') {
        this.scrollToTop();
        this.images.forEach(img => {
          img.shouldLoad = false;
          img.loading = true;
        });

        for (let i = 0; i < Math.min(this.parallelImageLoad, this.images.length); i++) {
          this.images[i].shouldLoad = true;
        }
      } else if (this.scrollType == 'scroll') {
        const previousImages = changes['images'].previousValue || [];
        const newImages = this.images.slice(previousImages.length);

        newImages.forEach(img => {
          img.shouldLoad = false;
          img.loading = true;
        });

        for (let i = 0; i < Math.min(this.parallelImageLoad, newImages.length); i++) {
          newImages[i].shouldLoad = true;
        }
      }

      this.cdr.detectChanges();
    }
  }

  onReorderToggle(event: boolean) {
    document.body.style.overflow = event ? 'hidden' : 'auto';
  }

  scrollToTop(): void {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTop = 0;
    }
  }

  onImageLoaded(index: number) {
    const image = this.images[index];
    image.loading = false;

    const stillLoading = this.images.filter(img => img.loading && img.shouldLoad).length;

    const nextToUnlock = this.images.filter(img => img.loading && !img.shouldLoad).slice(0, this.parallelImageLoad - stillLoading);

    nextToUnlock.forEach(img => {
      img.shouldLoad = true;
    });

    this.cdr.detectChanges();
  }

  onMouseDown(event: MouseEvent, image: ImageModel, index: number) {
    if (this.allowRearrange && image.selected) {
      this.startDrag(image, index);
    }
  }

  onTouchStart(event: TouchEvent, image: ImageModel, index: number) {
    this.longPressTriggered = false;
    this.touchPoints = event.touches.length;

    if (this.touchPoints > 1) {
      return;
    }

    if (this.allowRearrange && image.selected && this.isReorder) {
      this.startDrag(image, index);
    }

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;

    this.longPressTimer = setTimeout(() => {
      if (this.touchPoints > 1) {
        return;
      }
      image.selected = !image.selected;
      this.longPressTriggered = true;
      this.cdr.detectChanges();
    }, this.longPressDelay);
  }

  startDrag(image: ImageModel, index: number) {
    this.dragImageId = image.id;
    this.dragDisplayOrder = image.displayOrder;
    this.dragIndex = index;
    this.isDragging = true;
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    this.handleReorder(event.clientX, event.clientY);
  }

  onTouchMove(event: TouchEvent) {
    this.touchPoints = event.touches.length;

    this.handleDrag(event.touches[0]);

    if (this.touchPoints > 1 || this.zoom > 1) {
      return;
    }

    const moveX = event.touches[0].clientX;
    const moveY = event.touches[0].clientY;

    const deltaX = Math.abs(moveX - this.touchStartX);
    const deltaY = Math.abs(moveY - this.touchStartY);

    const isSwipe = deltaX > this.swipeThreshold || deltaY > this.swipeThreshold;

    if (isSwipe) {
      clearTimeout(this.longPressTimer);

      if (deltaY > 10) {
        return;
      }

      if (this.touchStartX > moveX && !this.lightboxOpen && !this.selectedImages.length) {
        this.onLeftSwipe.emit();
      }
      else if (this.touchStartX < moveX && !this.lightboxOpen && !this.selectedImages.length) {
        this.onRightSwipe.emit();
      }
    }

  }

  handleDrag(touch: Touch) {
    if (!this.isDragging) return;
    this.handleReorder(touch.clientX, touch.clientY);
  }

  handleReorder(x: number, y: number) {
    const target = document.elementFromPoint(x, y);
    const targetCard = target?.closest('.gallery-item');

    if (!targetCard) return;

    const allCards = Array.from(document.querySelectorAll('.gallery-item'));
    const overIndex = allCards.indexOf(targetCard as HTMLElement);

    if (overIndex !== -1 && overIndex !== this.dragIndex) {
      this.newDisplayOrder = this.images[overIndex].displayOrder;
      this.targetIndex = overIndex;

      const dragged = this.images[this.dragIndex!];
      this.images.splice(this.dragIndex!, 1);
      this.images.splice(overIndex, 0, dragged);
      this.dragIndex = overIndex;
    }
  }

  onMouseUp() {
    this.endDrag();
  }

  onMouseLeave() {
    clearTimeout(this.longPressTimer);
  }

  onTouchEnd() {
    this.endDrag();
    clearTimeout(this.longPressTimer);
  }

  endDrag() {
    if (this.isDragging) {
      if (this.dragImageId && this.newDisplayOrder) {
        this.onRearrange.emit({ imageId: this.dragImageId!, displayOrder: this.newDisplayOrder! });
        this.updateImageOrders(this.dragImageId!, this.newDisplayOrder!);
      }
    }
    this.isDragging = false;
    this.dragImageId = null;
    this.dragIndex = null;
    this.newDisplayOrder = null;
  }

  updateImageOrders(imageId: string, newDisplayOrder: number) {

    const image = this.images.find(img => img.id === imageId);
    if (!image) {
      console.error('Image not found.');
      return;
    }

    const oldDisplayOrder = image.displayOrder;

    if (newDisplayOrder === oldDisplayOrder) return;

    if (newDisplayOrder > oldDisplayOrder) {
      // Moving image down: decrement others in range (old+1 to new)
      this.images.forEach(img => {
        if (img.displayOrder > oldDisplayOrder && img.displayOrder <= newDisplayOrder) {
          img.displayOrder -= 1;
        }
      });
    } else {
      // Moving image up: increment others in range (new to old-1)
      this.images.forEach(img => {
        if (img.displayOrder >= newDisplayOrder && img.displayOrder < oldDisplayOrder) {
          img.displayOrder += 1;
        }
      });
    }

    // Set new displayOrder on the moved image
    image.displayOrder = newDisplayOrder;
  }

  onImageClick(image: ImageModel, index: number) {
    if (this.selectedImages.length > 0) {
      image.selected = !image.selected;
      return;
    }

    if (this.longPressTriggered || this.dragImageId == image.id) return;

    this.loadImageToLightbox(image, index);
  }

  openLightbox() {
    this.lightboxOpen = true;
    document.body.style.overflow = 'hidden';
    this.lightboxToggled.emit(true);
  }

  closeLightbox() {
    this.lightboxOpen = false;
    document.body.style.overflow = 'auto';
    this.lightboxToggled.emit(false);
  }

  loadImageToLightbox(image: ImageModel, index: number) {
    this.currentIndex = index;
    this.lightboxImage = image;

    if (!image.imageUrl) {
      this.lightboxImage.loading = true;
    }

    this.openLightbox();

    if (!this.modalStatePushed && !window.history.state.modalOpen) {
      window.history.pushState({ modalOpen: true }, '');
      this.modalStatePushed = true;
    }

    this.cdr.detectChanges();
  }

  onLightboxClose(isCloseButton = false) {
    this.disableSwipeNav = false;
    this.showImageNavButtons = true;

    if (this.modalStatePushed) {
      window.history.replaceState({}, '');
      this.modalStatePushed = false;
    }

    if (isCloseButton) {
      window.history.back();
    }

    this.closeLightbox();
  }
  /**
   * Preload the next and previous images after the current image is loaded in the lightbox.
   * This helps reduce perceived loading time when navigating images.
   */
  preloadAdjacentImages() {
    if (!this.images || this.images.length < 2) return;
    // Preload next image
    const nextIndex = (this.currentIndex + 1) % this.images.length;
    const nextImage = this.images[nextIndex];
    if (nextImage && nextImage.imageUrl && !nextImage.shouldLoad) {
      const img = new window.Image();
      img.src = nextImage.imageUrl;
      nextImage.shouldLoad = true;
    }
    // Preload previous image
    const prevIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    const prevImage = this.images[prevIndex];
    if (prevImage && prevImage.imageUrl && !prevImage.shouldLoad) {
      const img = new window.Image();
      img.src = prevImage.imageUrl;
      prevImage.shouldLoad = true;
    }
  }

  nextImage() {
    if (this.currentIndex == this.images.length - 1) {
      this.showSnackBar('No more images to load');
      return;
    }
    this.currentIndex = (this.currentIndex + 1) % this.images.length;

    const newImage = this.images[this.currentIndex];
    if (newImage.imageUrl) {
      this.lightboxImage = newImage;
      this.preloadAdjacentImages();
    }

    // Emit lightboxEnd event if it's the last 2 images
    if (this.currentIndex >= this.images.length - 2) {
      this.onLightboxEnd.emit();
    }

    this.cdr.detectChanges();
  }

  prevImage() {
    if (this.currentIndex == 0) {
      this.showSnackBar('You are viewing the first image');
      return;
    }
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    const newImage = this.images[this.currentIndex];
    if (newImage.imageUrl) {
      this.lightboxImage = newImage;
      this.preloadAdjacentImages();
    }
    this.cdr.detectChanges();
  }

  deleteImage(image: ImageModel) {
    this.onAdminDelete.emit(image);
  }

  onDeleteClicked(image: ImageModel) {
    this.onDelete.emit(image);
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: Event) {
    if (this.lightboxOpen) {
      this.onLightboxClose();
      event.stopPropagation();
      event.preventDefault();
      event.cancelBubble = true;
    }
  }

  shareImage(event: string) {
    this.onShare.emit(event);
  }

  preventContext(event: Event) {
    event.preventDefault();
  }

  toggleSelection(event: Event, image: ImageModel) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.allowDownload && !this.allowRearrange) {
      return;
    }

    image.selected = !image.selected;
    this.cdr.detectChanges();
  }

  get selectedImages(): ImageModel[] {
    return this.images.filter(img => img.selected);
  }

  clearSelections() {
    this.images.forEach(img => img.selected = false);
  }

  onDownloadSelectClick() {
    this.downloadSelectedImages.emit(this.selectedImages);
  }

  onDeleteSelectClick() {
    this.deleteSelectedImages.emit(this.selectedImages.map(x => x.id));
  }

  onDeleteMySelectClick() {
    this.deleteSelectedMyImages.emit(this.selectedImages.map(x => x.id));
  }

  downloadSelected() {
    const selected = this.selectedImages;
    let delay = 0;

    selected.forEach((img, index) => {
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = img.imageUrl;
        a.download = img.fileName || `image - ${index + 1}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }, delay);

      delay += 500;
    });
  }

  shareSelected() {
    if (!navigator.canShare || !navigator.canShare({ files: [] })) {
      alert('Sharing files is not supported on this browser.');
      return;
    }

    const fetchPromises = this.selectedImages.map(async (img) => {
      const response = await fetch(img.imageUrl);
      const blob = await response.blob();
      const fileName = img.fileName || 'shared-image.jpg';
      return new File([blob], fileName, { type: blob.type });
    });

    Promise.all(fetchPromises).then((files) => {
      const shareData: ShareData = {
        files: files,
        title: 'Wedding Memories',
        text: `Check out these amazing photos!`
      };

      navigator.share(shareData).catch((err) => {
        console.error('Sharing failed:', err);
      });
    });
  }

  showSnackBar(message: string): void {
    this.snackBar.open(message, 'Ok', {
      panelClass: ['custom-snackbar'],
      duration: 3000,
      verticalPosition: 'bottom',
      horizontalPosition: 'center'
    });
  }
}